import { FC, memo, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import styled, { css } from "styled-components";

import useDebounce from "@/hooks/useDebounce";
import { FilterableChannel, MinimalChannelResource } from "@/types/resources";
import { api } from "@/utils/api";
import SearchBar from "../SearchBar";
import Text from "../Text";
import Button from "@/components/Button";

const Card = styled.div<{ $opened: boolean }>`
  border-radius: 4px;
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.grayscale};
  margin: 0 0 16px;
  position: relative;

  ${({ $opened }) =>
    $opened &&
    css`
      border-bottom-color: transparent;
      border-bottom-left-radius: 0;
      border-bottom-right-radius: 0;
      box-shadow: 0px 3px 4px rgb(0 0 0 / 3%);
    `}
`;

const Header = styled.div`
  padding: 20px 24px;
  display: flex;
  align-items: center;
  cursor: pointer;
`;

const BottomActionsHolder = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 8px 0;
`;

const ToggleButton = styled.button<{ $rotate: boolean }>`
  flex-shrink: 0;
  margin-left: 90px;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  border: 1px solid ${({ theme }) => theme.colors.lightGray};
  transition: 0.2s ease-out border-color, 0.2s ease-out transform;

  &:hover {
    border-color: ${({ theme }) => theme.colors.black};
  }

  ${({ $rotate }) =>
    $rotate &&
    css`
      transform: rotate(180deg);
    `}
`;

const HeaderContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const StyledButton = styled(Button)`
  font-size: 14px;
  min-width: 120px;
  height: 38px;
  padding: 0 20px;
`;

const ApplyButton = styled(StyledButton)`
  margin-left: 16px;
`;

const Body = styled.div`
  position: absolute;
  z-index: ${({ theme }) => theme.zIndex[2]};
  left: -1px;
  top: 100%;
  width: calc(100% + 2px);
  box-shadow: 0px 3px 4px rgba(0, 0, 0, 0.03);
  border-bottom-left-radius: 4px;
  border-bottom-right-radius: 4px;
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.grayscale};
  border-top-color: ${({ theme }) => theme.colors.lightGray};
  padding: 24px;
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  grid-row-gap: 24px;
`;

const BodyHeader = styled.div`
  display: flex;
  align-items: center;
`;

const BodyHeaderLine = styled.div`
  height: 1px;
  background-color: ${({ theme }) => theme.colors.grayscaleLight};
  flex: 1;
  margin: 0 16px;
`;

const Title = styled(Text).attrs({
  $type: "smallBold",
  $color: "black",
  as: "h5",
})`
  margin-bottom: 12px;
  text-transform: uppercase;
`;

const InnerCard = styled.div`
  padding: 16px;
  border: 1px solid ${({ theme }) => theme.colors.grayscale};
  border-radius: 4px;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  min-height: 64px;
`;

interface InnerCardToggableProps {
  $expanded?: boolean;
  $toggable?: boolean;
}

const InnerCardToggable = styled(InnerCard)<InnerCardToggableProps>`
  position: relative;
  max-height: 248px;
  overflow: hidden;
  ${({ $expanded }) =>
    $expanded &&
    css`
      max-height: none;
    `}
  ${({ $toggable }) =>
    $toggable &&
    css`
      padding-bottom: 49px;
    `}
`;

const StyledSearchBar = styled(SearchBar)`
  margin: 0;
`;

const SelectedChannelsHeader = styled.div`
  display: flex;
  align-items: baseline;
  margin-bottom: 12px;
  flex: 1;
  ${Title} {
    margin-bottom: 0;
    margin-right: auto;
  }
`;

const ResetButton = styled.button`
  background: ${({ theme }) => theme.colors.errorLight};
  height: 23px;
  display: flex;
  align-items: center;
  padding: 0 16px;
  border-radius: 4px;
  border: 1px solid transparent;
  transition: all ease-out 0.2s;
  &:hover,
  &:active {
    border-color: ${({ theme }) => theme.colors.error};
  }
  &:disabled {
    background: ${({ theme }) => theme.colors.grayscaleLight};
    cursor: not-allowed;
    &:hover,
    &:active {
      border-color: transparent;
    }
  }
`;

const ChannelListButton = styled(Text).attrs({
  as: "button",
  $type: "smallBold",
})`
  height: 30px;
  display: inline-flex;
  padding: 0 12px;
  background: ${({ theme }) => theme.colors.grayscaleLight};
  border: 1px solid ${({ theme }) => theme.colors.grayscaleLight};
  border-radius: 4px;
  align-items: center;
  transition: 0.2s ease-out color, 0.2s ease-out border-color;
  &:hover {
    border-color: ${({ theme }) => theme.colors.purple};
    color: ${({ theme }) => theme.colors.purple};
  }
  ${({ theme, $color }) =>
    $color === "purple" &&
    css`
      border-color: ${theme.colors.purple};
      color: ${theme.colors.purple};
    `}
`;

const EmptyListTitle = styled(Text).attrs({
  $color: "gray",
  $type: "smallBold",
})`
  text-align: center;
  width: 100%;
`;

const SelectedChannelsAction = styled(Text).attrs({
  $type: "smallBold",
})`
  margin-left: 24px;
  transition: 0.2s ease-out opacity;
  &:not([disabled]):hover {
    opacity: 0.7;
  }
  &:disabled {
    cursor: not-allowed;
    color: ${({ theme }) => theme.colors.gray};
  }
`;

const ChannelsCount = styled(Text)`
  font-weight: 500;
`;

const ShowMoreHolder = styled.div`
  position: absolute;
  left: 0;
  bottom: 0;
  background-color: ${({ theme }) => theme.colors.white};
  width: 100%;
  text-align: center;
  padding: 6px 0;
`;

const ShowMoreButton = styled.button<{ $expanded?: boolean }>`
  padding: 8px 12px;
  font-family: "Manrope", sans-serif;
  font-weight: 700;
  font-size: 12px;
  line-height: 16px;
  border: 1px solid transparent;
  border-radius: 50px;
  transition: border-color ease-out 0.2s;
  background-color: ${({ theme }) => theme.colors.white};
  min-width: 110px;
  svg {
    margin-right: 8px;
    transition: transform ease-out 0.2s;
  }
  &:hover {
    border-color: ${({ theme }) => theme.colors.lightGray};
  }
  ${({ $expanded }) =>
    $expanded &&
    css`
      > svg {
        transform: rotate(180deg);
      }
    `}
`;

interface Props {
  className?: string;
  setChannels: (channels: number[]) => void;
}

const FacebookPagesFilter: FC<Props> = ({ className, setChannels }) => {
  const [isOpened, setIsOpened] = useState(false);

  const [selectedChannels, setSelectedChannels] = useState<FilterableChannel[]>(
    []
  );

  const [areChannelsExpanded, setAreChannelsExpanded] = useState(false);

  const [channelsListHeight, setChannelsListHeight] = useState(0);

  const [search, setSearch] = useState("");

  const debouncedSearch = useDebounce(search, 350);

  const channelsListRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (!channelsListRef.current) return;
    const { height } = channelsListRef.current.getBoundingClientRect();
    setChannelsListHeight(height);
  }, [isOpened]);

  const onClose = () => {
    setChannels(selectedChannels.map((c) => c.id));
    setIsOpened((prev) => !prev);
  };

  const {
    data: channels,
    isLoading,
    isError,
  } = useQuery(
    ["channels", { onlyBp: true, authorized: true }],
    () =>
      api.get<never, MinimalChannelResource[]>(`/v1/channels/authorized`, {
        params: {
          only_bp: 1,
        },
      }),
    {
      staleTime: 1000 * 120,
    }
  );

  const filteredChannels = useMemo(() => {
    const lowerCaseSearch = debouncedSearch.toLocaleLowerCase();

    const filterChannelsCallback = (value: MinimalChannelResource) => {
      if (!lowerCaseSearch) {
        return true;
      }

      return value.name.toLocaleLowerCase().includes(lowerCaseSearch);
    };

    return channels?.filter(filterChannelsCallback);
  }, [channels, debouncedSearch]);

  if (isLoading || isError) return null;

  return (
    <Card $opened={isOpened} className={className}>
      <Header onClick={onClose}>
        <HeaderContent>
          <Text $type="button" as="div">
            Channels{" "}
            <ChannelsCount $type="button" $color="gray" as="span">
              ({selectedChannels.length || channels?.length} channel
              {selectedChannels.length !== 1 && "s"}{" "}
              {!!selectedChannels.length && "selected"})
            </ChannelsCount>
          </Text>
        </HeaderContent>
        <ToggleButton $rotate={isOpened}>
          <svg
            width="8"
            height="6"
            viewBox="0 0 8 6"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M4 5.5L8 0.5H0L4 5.5Z" fill="currentColor" />
          </svg>
        </ToggleButton>
      </Header>
      {isOpened && (
        <Body>
          <BodyHeader>
            <Text $type="button">Filter</Text>
            <BodyHeaderLine />
            <ResetButton
              disabled={!selectedChannels.length}
              onClick={() => {
                setSelectedChannels([]);
              }}
            >
              <Text
                $color={!selectedChannels.length ? "gray" : "error"}
                $type="smallHeadline"
              >
                Reset
              </Text>
            </ResetButton>
          </BodyHeader>
          <div>
            <Title>Search By Channels</Title>
            <StyledSearchBar value={search} setValue={setSearch} />
          </div>
          <div>
            <SelectedChannelsHeader>
              <Title>Channels List</Title>
              <SelectedChannelsAction
                as="button"
                onClick={() =>
                  setSelectedChannels((prev) => [...prev, ...filteredChannels!])
                }
                disabled={filteredChannels!.length === 0}
              >
                Select All
              </SelectedChannelsAction>
              <SelectedChannelsAction
                as="button"
                onClick={() => setSelectedChannels([])}
                disabled={selectedChannels.length === 0}
              >
                Clear
              </SelectedChannelsAction>
            </SelectedChannelsHeader>
            <InnerCardToggable
              ref={channelsListRef}
              $expanded={areChannelsExpanded}
              $toggable={channelsListHeight >= 248}
            >
              {filteredChannels?.length ? (
                filteredChannels?.map((channel) => (
                  <ChannelListButton
                    key={channel.id}
                    onClick={() => {
                      if (selectedChannels.includes(channel)) {
                        return setSelectedChannels((prev) =>
                          prev.filter((element) => element !== channel)
                        );
                      }
                      setSelectedChannels((prev) => [...prev, channel]);
                    }}
                    $color={
                      selectedChannels.includes(channel) ? "purple" : "black"
                    }
                  >
                    {channel.name}
                  </ChannelListButton>
                ))
              ) : (
                <EmptyListTitle>No Channels To Select</EmptyListTitle>
              )}
              {channelsListHeight >= 248 && (
                <ShowMoreHolder>
                  <ShowMoreButton
                    $expanded={areChannelsExpanded}
                    onClick={() => setAreChannelsExpanded((prev) => !prev)}
                  >
                    <svg
                      width="11"
                      height="8"
                      viewBox="0 0 11 8"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M10 1.75L5.5 6.25L1 1.75"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    {areChannelsExpanded ? "Show less" : "Show more"}
                  </ShowMoreButton>
                </ShowMoreHolder>
              )}
            </InnerCardToggable>
          </div>
          <BottomActionsHolder>
            <StyledButton onClick={onClose} color="secondary">
              Cancel
            </StyledButton>
            <ApplyButton
              disabled={!!!selectedChannels?.length}
              color="colorized"
              onClick={onClose}
            >
              Apply filter
            </ApplyButton>
          </BottomActionsHolder>
        </Body>
      )}
    </Card>
  );
};

export default memo(FacebookPagesFilter);
